import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { unitId, actionId } from '@/annotations'
import { Button } from '@/components/Button'
import { Chip } from '@/components/Chip'
import { IconButton } from '@/components/IconButton'
import { Note } from '@/components/Note'
import { Alert as AlertBanner } from '@/components/Alert'
import { GatedControl } from '@/components/GatedControl'
import { BarChart } from '@/components/BarChart'
import { DonutChart } from '@/components/DonutChart'
import { getApiClient, unwrap, ApiProblemError } from '@/data/apiClient'
import { UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'
import { useActiveOrgId, useActiveWorkspaceId, setActiveWorkspace } from '@/data/store'
import { useCapabilitySet, hasWorkspaceCapability } from '@/data/capabilities'
import { formatJobTime } from '@/screens/shared/jobFormatting'
import { GridTable } from '@/components/GridTable'
import type { components } from '@/api/generated/schema'
import type { Problem } from '@/data/apiClient'

type BackupSummary = components['schemas']['BackupSummary']
type WorkspaceSummary = components['schemas']['WorkspaceSummary']
type CredentialHealth = components['schemas']['CredentialHealth']
type JobRef = components['schemas']['JobRef']
type Alert = components['schemas']['Alert']
type RepositoryScope = components['schemas']['RepositoryScope']

const SEVERITY_VARIANT: Record<string, 'danger' | 'warn' | 'info'> = { CRITICAL: 'danger', HIGH: 'danger', MEDIUM: 'warn', INFO: 'info' }

const PERIODS = [7, 30, 90] as const
const KIND_LABEL: Record<string, string> = { DATA_BACKUP: 'Data backup', CONFIG_BACKUP: 'Configuration backup' }
const CREDENTIAL_LABEL: Record<string, string> = { PENDING: 'Pending', VALID: 'Valid', INVALID: 'Invalid', EXPIRING_SOON: 'Expiring soon', SCOPES_REDUCED: 'Scopes reduced' }
const PROTECTION_CHIP: Record<string, 'active' | 'warning' | 'pending' | 'failure'> = { PROTECTED: 'active', NOT_YET_PROTECTED: 'warning', SETUP_UNFINISHED: 'pending', BLOCKED: 'failure' }

/**
 * `v-dashboard` — process/09-SCREEN-SPECS/v-dashboard.md. Every figure below is read straight off
 * `BackupSummary`/`WorkspaceSummary` — none of it is totalled or derived client-side (rule 1), and
 * *no grants* is never rendered the same as *every workspace* (rule 2, `hasWorkspaceCapability`).
 *
 * **The per-day `bar-chart`/`donut-chart` are built (follow-up pass, visual-diff sweep finding)**:
 * `BackupSummary.daily` was fetched and typed but never rendered — rule 5 of this screen's own spec
 * names the chart/donut as served, not client-computed, so both are drawn straight off
 * `daily`/`buckets[].success_rate` with no client-side totalling. Scoped to the `DATA_BACKUP` kind
 * only (the prototype's own default toggle state) — a real `CONFIG_BACKUP`/kind segmented-control
 * to switch both charts is a follow-up, not built here.
 *
 * **`link`/`grid-tools`/`grid-footer`/`tools` added W160.** The workspace table's own two `link`s
 * are both real: the workspace name switches `setActiveWorkspace` (the same store setter the
 * topbar's own switcher already uses) when it isn't already the active one; the last-backup time
 * links to `/jobs/backup/:jobId` (`w.last_backup` is a real `JobRef`, the same route `v-jobsbackup`
 * already uses). `grid-tools` on the Workspaces panel carries a real "Refresh" `icon-button`; the
 * Outcomes panel's segmented-control is now wrapped in `data-cmp="tools"`, matching the spec's own
 * "segmented-control in tools" — no new control, the correct wrapper on one already there.
 *
 * **Every chip on this screen recoloured against the prototype's own dashboard markup (W172)**:
 * the bucket stat chips were all flat grey (`succeeded`/`partial`/`failed`/`skipped`/`cancelled`
 * now use the same real semantic the backup-jobs bar chart's own legend draws — green/orange/red/
 * grey/grey); the workspace protection chip was a `PROTECTED`-only blue/grey binary, now
 * `active`/`warning`/`pending` for the three states the prototype's own "Workspaces in this
 * organisation" table shows (`PROTECTED`/`NOT_YET_PROTECTED`/`SETUP_UNFINISHED`). `BLOCKED` has no
 * row in the prototype; mapped to `failure` as the most severe of the four by inference, not by a
 * direct example.
 *
 * **"Storage stats" built for real W177 — served data, fetched, never rendered.** `design/
 * 27-ui-surface-and-navigation.md` §27.3's own composition table lists it as sourced from the
 * `backup_jobs` roll-up, "served with its display string," and **not** one of the two panels §27.3
 * marks "Blocked on §24.2b G3" (the donut and the per-day history, which this screen already
 * correctly declines to build). `BackupSummary.storage` (`retained_jobs`/`stored_bytes`, both
 * `Quantity`) was already in the `summary` query's own response type and simply never read.
 * Labelled **Retained jobs**, not the prototype's own **Retained snapshots** — the schema's own doc
 * comment states the prototype's label is the wrong noun (it counts capture jobs, not snapshots,
 * and doubles under two job kinds), so the corrected label is used rather than the drawn one.
 *
 * **The prototype's "Organisation" panel (State / Workspaces protected / Workspace slots used /
 * Workspace users / Repositories) is NOT built, field by field, checked rather than assumed
 * (W177).** *Workspace slots used* has no server concept anywhere in this contract — §27.1's own
 * footnote records Plan and Billing as removed from MVP entirely, and no schema anywhere carries a
 * workspace-slot limit. *Workspace users* has no total: `organisationListUsers` is a cursor-paged
 * list (§32.8/rule 25 forbid a total on one). *Repositories* (org-wide) would need summing
 * `WorkspaceSummary.repository_count` across every workspace — a client-composed aggregate of
 * exactly the kind rule 24 forbids; the field is deliberately a per-workspace denominator (`D91`,
 * §27.3b), never authorised as an org-wide sum. *State* is knowable (`Organisation.access_blocked`)
 * but would need a new fetch this screen doesn't otherwise make, for a value that is close to
 * tautological here — a blocked organisation refuses every request (§3.3), so reaching this screen
 * at all already implies it. *Workspaces protected* is the one field a complete, already-fetched
 * list could answer without a new request, and was still left out rather than counted client-side —
 * consistent with the same rule the other four fail, not an exception carved out for it.
 */
export function VDashboard() {
  const orgId = useActiveOrgId()!
  const workspaceId = useActiveWorkspaceId()!
  const queryClient = useQueryClient()
  const caps = useCapabilitySet()
  const [periodDays, setPeriodDays] = useState<(typeof PERIODS)[number]>(7)
  const [job, setJob] = useState<JobRef | undefined>()

  const summary = useQuery({
    queryKey: ['backup-summary', orgId, workspaceId, periodDays],
    // Found live, 2026-09-25 (systemic audit after VHealth.tsx/VDrift.tsx's own fix) — this is the
    // app's own landing screen, the single most likely place to mount before `ShellLayer`'s
    // `setActiveOrg`/workspace-selection effects (which run after the first render) have settled.
    enabled: Boolean(orgId && workspaceId),
    queryFn: async (): Promise<BackupSummary> => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/backups/summary', {
        params: { path: { org_id: orgId, workspace_id: workspaceId }, query: { period_days: periodDays } },
      })
      if (error) throw new ApiProblemError(error as Problem)
      return data as unknown as BackupSummary
    },
  })

  const workspaces = useQuery({
    queryKey: ['workspaces', orgId],
    enabled: Boolean(orgId),
    queryFn: async () => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces', { params: { path: { org_id: orgId } } })
      if (error) throw new ApiProblemError(error as Problem)
      return (data.items ?? []) as unknown as readonly WorkspaceSummary[]
    },
  })

  const credentialHealth = useQuery({
    queryKey: ['credential-health', orgId, workspaceId],
    enabled: Boolean(orgId && workspaceId),
    queryFn: async (): Promise<CredentialHealth> => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/credential/health', {
        params: { path: { org_id: orgId, workspace_id: workspaceId } },
      })
      if (error) throw new ApiProblemError(error as Problem)
      return data as unknown as CredentialHealth
    },
  })

  const trigger = useMutation({
    mutationFn: async () => {
      const client = getApiClient()
      const response = await client.POST('/orgs/{org_id}/workspaces/{workspace_id}/backups', {
        params: { path: { org_id: orgId, workspace_id: workspaceId } },
        body: { backup_kind: 'DATA_BACKUP' },
        headers: { 'Idempotency-Key': crypto.randomUUID() },
      })
      return unwrap(response as { data?: JobRef; error?: Problem })
    },
    onSuccess: setJob,
  })

  // design/27-ui-surface-and-navigation.md §27.3's own dashboard composition table — "Needs
  // attention" (§21) and a "Not protected" badge (§12.5) — neither of which `v-dashboard.md`'s
  // own Operations table ever carried through into a buildable contract (a spec-authoring gap
  // predating this build, not a frontend one). Added here as a deliberate, flagged exception
  // rather than left orphaned, per Sanket's own direction (W153).
  const alerts = useQuery({
    queryKey: ['alerts', orgId, workspaceId],
    enabled: Boolean(orgId && workspaceId),
    queryFn: async () => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/alerts', { params: { path: { org_id: orgId, workspace_id: workspaceId } } })
      if (error) throw new ApiProblemError(error as Problem)
      return (data.items ?? []) as unknown as readonly Alert[]
    },
  })

  // Prototype's own alert cards carry a "Restore"/"Acknowledge" action row (`Prototype_v2.html`
  // `#v-dashboard`). "Restore" needs a job to restore from, and `Alert` carries no job reference —
  // a genuine contract gap, not built. "Acknowledge" is a real, already-contracted operation
  // (`alertAcknowledge`, `x-capability: ALERT_MANAGE`) and is wired here for real.
  const acknowledge = useMutation({
    mutationFn: async (alertId: string) => {
      const client = getApiClient()
      const response = await client.POST('/orgs/{org_id}/workspaces/{workspace_id}/alerts/{alert_id}/acknowledge', {
        params: { path: { org_id: orgId, workspace_id: workspaceId, alert_id: alertId } },
      })
      return unwrap(response as { data?: Alert; error?: Problem })
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['alerts', orgId, workspaceId] }),
  })

  const scope = useQuery({
    queryKey: ['workspace-scope', orgId, workspaceId],
    enabled: Boolean(orgId && workspaceId),
    queryFn: async (): Promise<RepositoryScope> => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/scope', { params: { path: { org_id: orgId, workspace_id: workspaceId } } })
      if (error) throw new ApiProblemError(error as Problem)
      return data as unknown as RepositoryScope
    },
  })

  const summaryProblem = summary.error instanceof ApiProblemError ? summary.error.problem : undefined
  const workspacesProblem = workspaces.error instanceof ApiProblemError ? workspaces.error.problem : undefined
  const allowed = hasWorkspaceCapability(caps, workspaceId, 'BACKUP_RUN')
  const navigate = useNavigate()

  return (
    <div data-scr={unitId('v-dashboard')}>
      <div data-cmp="panel-header" className="flex items-center gap-5 mb-8">
        <h1 className="m-0 text-section font-semibold flex-1">Backup job stats</h1>
        <Button data-act={actionId('nav.ob-wz-1')} ghost onClick={() => navigate('/onboarding')}>
          Connect a new workspace
        </Button>
      </div>

      {alerts.data && alerts.data.length > 0 && (
        <div data-cmp="panel" className="bg-panel border border-bd rounded-2 p-6 mb-7">
          <div data-cmp="panel-header" className="flex items-center gap-5 mb-4">
            <h3 className="m-0 text-body-lg font-semibold flex-1">Needs attention</h3>
            <button type="button" data-act={actionId('nav.v-alerts')} className="underline text-link bg-transparent border-0 p-0 cursor-pointer text-small" onClick={() => navigate('/alerts')}>
              View all
            </button>
          </div>
          <div className="flex flex-col gap-3">
            {alerts.data.slice(0, 3).map((a) => (
              <AlertBanner key={a.alert_id} variant={SEVERITY_VARIANT[a.severity] ?? 'info'}>
                <div className="flex items-start justify-between gap-4">
                  <span>{a.statement}</span>
                  {a.state === 'OPEN' && (
                    <Button
                      data-act={actionId('alert.acknowledge')}
                      ghost
                      disabled={acknowledge.isPending}
                      onClick={() => acknowledge.mutate(a.alert_id)}
                    >
                      Acknowledge
                    </Button>
                  )}
                </div>
              </AlertBanner>
            ))}
          </div>
        </div>
      )}

      {scope.data && scope.data.exclusions.length > 0 && (
        <div className="mb-7">
          <button type="button" data-act={actionId('nav.v-notprotected')} onClick={() => navigate('/notprotected')} className="inline-flex items-center gap-3 bg-transparent border-0 p-0 cursor-pointer">
            <Chip variant="plain">{scope.data.exclusions.length} not protected</Chip>
          </button>
        </div>
      )}

      {credentialHealth.data && credentialHealth.data.status !== 'VALID' && (
        <div className="mb-7">
          <AlertBanner variant={credentialHealth.data.status === 'INVALID' ? 'danger' : 'warn'}>
            Credential status: {CREDENTIAL_LABEL[credentialHealth.data.status] ?? credentialHealth.data.status}. Whatever this says, restore and reports keep
            working — only capture stops.
          </AlertBanner>
        </div>
      )}

      <div data-cmp="panel" className="bg-panel border border-bd rounded-2 p-6 mb-8">
        <div data-cmp="panel-header" className="flex items-center gap-5 mb-6">
          <h3 className="m-0 text-body-lg font-semibold flex-1">Outcomes</h3>
          <div data-cmp="tools">
            <div data-cmp="segmented-control" className="flex gap-2">
              {PERIODS.map((p) => (
                <button
                  key={p}
                  type="button"
                  data-cmp="segment-option"
                  onClick={() => setPeriodDays(p)}
                  className={`py-2 px-4 rounded-1 text-small font-semibold ${p === periodDays ? 'bg-blue text-white' : 'bg-row text-tx2'}`}
                >
                  {p} days
                </button>
              ))}
            </div>
          </div>
        </div>

        {summaryProblem && (isUnexpectedRefusal(summaryProblem.refusal_kind) ? <UnexpectedRefusal problem={summaryProblem} /> : <Note>{summaryProblem.title}</Note>)}

        <div className="grid gap-5" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}>
          {summary.data?.buckets.map((b) => (
            <div key={b.backup_kind} data-cmp="kv-row" className="border border-bd rounded-2 p-5">
              <div className="text-body-lg font-semibold mb-3">{KIND_LABEL[b.backup_kind] ?? b.backup_kind}</div>
              <div className="text-small text-muted mb-2">{b.success_rate.caption}</div>
              <div className="flex flex-wrap gap-2">
                <Chip variant="active">{b.succeeded} succeeded</Chip>
                <Chip variant="warning">{b.partial} partial</Chip>
                <Chip variant="failure">{b.failed} failed</Chip>
                <Chip variant="grey">{b.skipped} skipped</Chip>
                <Chip variant="grey">{b.cancelled} cancelled</Chip>
                <Chip variant="plain">of {b.expected} expected</Chip>
              </div>
            </div>
          ))}
        </div>
      </div>

      {summary.data?.daily && summary.data.daily.length > 0 && (
        <div className="grid gap-5 mb-8" style={{ gridTemplateColumns: '1fr 300px' }}>
          <div data-cmp="panel" className="bg-panel border border-bd rounded-2 p-6">
            <div data-cmp="panel-header" className="mb-4">
              <h3 className="m-0 text-body-lg font-semibold">Backup jobs history</h3>
            </div>
            <BarChart days={summary.data.daily.filter((d) => d.backup_kind === 'DATA_BACKUP').map((d) => ({ date: d.date, succeeded: d.succeeded, partial: d.partial, failed: d.failed }))} />
          </div>
          <div data-cmp="panel" className="bg-panel border border-bd rounded-2 p-6">
            <div data-cmp="panel-header" className="mb-4">
              <h3 className="m-0 text-body-lg font-semibold">Backup jobs summary</h3>
            </div>
            {(() => {
              const bucket = summary.data.buckets.find((b) => b.backup_kind === 'DATA_BACKUP')
              if (!bucket) return null
              return (
                // Centre figure is the served numerator/denominator, concatenated — never a
                // client-computed percentage (rule 1/§24.2 rule 24 forbid deriving a ratio here).
                <DonutChart
                  percentDisplay={`${bucket.success_rate.numerator}/${bucket.success_rate.denominator}`}
                  centerSub="Succeeded"
                  segments={[
                    { label: 'Success', value: bucket.succeeded, colorVar: '--ok' },
                    { label: 'Partial', value: bucket.partial, colorVar: '--warn' },
                    { label: 'Failed', value: bucket.failed, colorVar: '--fail' },
                  ]}
                  caption={`${bucket.expected} data backup jobs — ${bucket.success_rate.caption}.`}
                />
              )
            })()}
          </div>
        </div>
      )}

      {summary.data?.storage && (
        <div data-cmp="panel" className="bg-panel border border-bd rounded-2 p-6 mb-8">
          <div data-cmp="panel-header" className="mb-4">
            <h3 className="m-0 text-body-lg font-semibold">Storage stats</h3>
          </div>
          <div className="flex flex-wrap gap-8">
            {summary.data.storage.retained_jobs && (
              <div data-cmp="kv-row">
                <div className="text-small text-muted mb-1">Retained jobs</div>
                <div className="text-body-lg font-semibold">{summary.data.storage.retained_jobs.display}</div>
              </div>
            )}
            {summary.data.storage.stored_bytes && (
              <div data-cmp="kv-row">
                <div className="text-small text-muted mb-1">Stored objects</div>
                <div className="text-body-lg font-semibold">{summary.data.storage.stored_bytes.display}</div>
              </div>
            )}
          </div>
        </div>
      )}

      <div data-cmp="panel" className="bg-panel border border-bd rounded-2 p-0">
        <div data-cmp="panel-header" className="flex items-center gap-5 p-6 border-b border-bd">
          <h3 className="m-0 text-body-lg font-semibold flex-1">Workspaces</h3>
          <div data-cmp="grid-tools" className="flex items-center gap-4">
            <IconButton
              data-act={undefined}
              icon="i-refresh"
              label="Refresh"
              disabled={workspaces.isFetching}
              onClick={() => queryClient.invalidateQueries({ queryKey: ['workspaces', orgId] })}
            />
            <GatedControl allowed={allowed} reason="Available on the Operator role and above">
              <Button data-act={actionId('job.trigger')} disabled={trigger.isPending} onClick={() => trigger.mutate()}>
                Back up now
              </Button>
            </GatedControl>
          </div>
        </div>

        {job && (
          <div className="p-6">
            <AlertBanner variant="info">
              Backup started — job <span className="font-mono">{job.handle}</span> is now running.
            </AlertBanner>
          </div>
        )}

        {workspacesProblem && (
          <div className="p-6">
            {isUnexpectedRefusal(workspacesProblem.refusal_kind) ? <UnexpectedRefusal problem={workspacesProblem} /> : <Note>{workspacesProblem.title}</Note>}
          </div>
        )}

        <GridTable
          columns={[
            {
              key: 'workspace',
              header: 'Workspace',
              render: (w) =>
                w.workspace_id === workspaceId ? (
                  w.name
                ) : (
                  <button
                    type="button"
                    data-cmp="link"
                    className="underline text-link bg-transparent border-0 p-0 cursor-pointer text-body-lg"
                    onClick={() => setActiveWorkspace(w.workspace_id)}
                    title="Make this the active workspace"
                  >
                    {w.name}
                  </button>
                ),
            },
            {
              key: 'protection',
              header: 'Protection',
              render: (w) => (
                <div className="flex items-center gap-3">
                  <Chip variant={PROTECTION_CHIP[w.protection_state.state] ?? 'plain'}>{w.protection_state.display}</Chip>
                  {/* Found live (Sanket, 2026-09-25): a workspace can sit here `SETUP_UNFINISHED`
                      indefinitely with no way back into the wizard — `OnboardingLayer`'s own
                      `/onboarding` route always restarted stage 1, which dead-ends an
                      already-connected workspace on `WORKSPACE_ALREADY_CONNECTED`.
                      `/onboarding/:workspaceId` resumes it instead (see that route's own comment
                      in `ShellLayer.tsx`). */}
                  {w.protection_state.state === 'SETUP_UNFINISHED' && (
                    <button
                      type="button"
                      data-cmp="link"
                      className="underline text-link bg-transparent border-0 p-0 cursor-pointer text-small"
                      onClick={() => navigate(`/onboarding/${w.workspace_id}`)}
                    >
                      Finish setup
                    </button>
                  )}
                </div>
              ),
            },
            {
              key: 'lastBackup',
              header: 'Last backup',
              render: (w) =>
                w.last_backup ? (
                  <button
                    type="button"
                    data-cmp="link"
                    className="underline text-link bg-transparent border-0 p-0 cursor-pointer text-body-lg"
                    onClick={() => navigate(`/jobs/backup/${w.last_backup!.job_id}`)}
                  >
                    {formatJobTime(w.last_backup.started_at)}
                  </button>
                ) : (
                  <span className="text-muted">Never</span>
                ),
            },
            { key: 'nextWindow', header: 'Next window', render: (w) => (w.next_window ? w.next_window.display : '—') },
            { key: 'credential', header: 'Credential', render: (w) => (w.credential_status ? (CREDENTIAL_LABEL[w.credential_status] ?? w.credential_status) : '—') },
            { key: 'trial', header: 'Trial ends', render: (w) => (w.trial_ends_at ? formatJobTime(w.trial_ends_at) : '—') },
          ]}
          rows={workspaces.data ?? []}
          getRowKey={(w) => w.workspace_id}
          emptyMessage={workspaces.isLoading ? '' : 'No workspaces match these filters.'}
        />

        <div data-cmp="grid-footer" className="border-t border-bd p-6">
          <Note>Every workspace in the organisation — not paged, not filtered.</Note>
        </div>
      </div>
    </div>
  )
}
